import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../state/state';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { closeModal, toggleLoading } from '../../../state/ModalSlice/ModalSlice';
import { db } from '../../../Config/firebase';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { ExportType } from '../types/ExportTypes';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';

const ExportPaymentsModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.ModalSlice.ExportPaymentsModalOpen);
    const providerId: string = useAppSelector(selectEffectiveUID)   
    const isLoading = useAppSelector((state) => state.ModalSlice.isLoading);

    const today = new Date();
    const thirtyDaysBefore = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const [formData, setFormData] = useState({
        StartDate: thirtyDaysBefore.toISOString().split('T')[0],
        EndDate: today.toISOString().split('T')[0],
    });

    const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, StartDate: event.target.value }));
    };

    const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, EndDate: event.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(toggleLoading(true));
        try {
            const ExportData = {
                StartDate: Timestamp.fromDate(new Date(formData.StartDate + 'T00:00:00')),
                EndDate: Timestamp.fromDate(new Date(formData.EndDate + 'T23:59:59')),
                RequestedOn: Timestamp.fromDate(new Date()),
                Status: "Pending",
                exportType: ExportType.PAYMENTS
            };
            
            const ExportsCollectionRef = collection(db, 'ServiceProviders', providerId, 'Exports');
            await addDoc(ExportsCollectionRef, ExportData);
            handleClose();
        } catch (error) {
            console.error("Error adding export:", error);
        } finally {
            dispatch(toggleLoading(false));
        }
    };

    const handleClose = () => {
        dispatch(closeModal('ExportPaymentsModalOpen'));
        setFormData({
            StartDate: thirtyDaysBefore.toISOString().split('T')[0],
            EndDate: today.toISOString().split('T')[0],
        });
    };

    return (
        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                    margin: { xs: '16px', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                },
            }}
            maxWidth="sm"
            fullWidth
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: { xs: "16px", sm: "24px", md: "60px" }, paddingY: { xs: "24px", sm: "40px", md: "50px" }, maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' }, overflowY: 'auto' }} className={"bg-white"}>
                <div className={"w-full max-w-full"}>
                    <div className={"header pb-3 sm:pb-[20px]"}>
                        <p className={"text-base sm:text-lg font-[600]"}>Export Payment Data</p>
                        <p className={"text-xs sm:text-sm font-[400]"}>Select date range for payment data export</p>
                    </div>

                    <div className={"absolute right-2 sm:right-4 top-2 sm:top-4"}>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>

                    <div className={"pt-0"}>
                        <Box component={"div"}>
                            <div className="flex flex-col gap-4">
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={formData.StartDate}
                                    onChange={handleChangeStartDate}
                                    fullWidth
                                    size='small'
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        fontWeight: 400,
                                        width: { xs: '100%', sm: '100%' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={formData.EndDate}
                                    onChange={handleChangeEndDate}
                                    fullWidth
                                    size='small'
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        },
                                        fontWeight: 400,
                                        width: { xs: '100%', sm: '100%' },
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <div className={"flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4"}>
                                    <button
                                        disabled={isLoading}
                                        onClick={handleClose}
                                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className={`w-full bg-cc_brand text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border-gray-500 flex justify-center items-center text-xs sm:text-sm ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        {isLoading && <CircularProgress sx={{ color: "white" }} size={16} className="sm:w-5 sm:h-5" />}
                                        <p className={"ml-1.5 sm:ml-2"}>{isLoading ? "Loading" : "Start Export"}</p>
                                    </button>
                                </div>
                            </div>
                        </Box>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ExportPaymentsModal; 